import { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Sheet,
    SheetClose,
    SheetContent,
    SheetDescription,
    SheetHeader,
    SheetTitle,
} from '@/components/ui/sheet';
import { Skeleton } from '@/components/ui/skeleton';
import { Toaster } from 'sonner';
import { cn } from '@/lib/utils';
import useMediaQuery from '@/hooks/useMediaQuery';
import { AppConfig } from '@/config/app.config';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import MoveLeftIcon from '@/components/icons/MoveLeftIcon';
import Cancel_01_Icon from '@/components/icons/Cancel_01_Icon';
import { useFinancesBalanceApi } from '../../hooks/useFinancesBalanceApi';
import { useFinancesPageFilter } from '../../store/useFinancesPageFilter';
import { FinancesPageFilterFormSchema } from '../../validators/FinancesPageFilterValidator';
import SettlementsDetailsSkeleton from './SettlementsDetailsSkeleton';
import SettlementsElement from './SettlementsElement';
import DoorIcon from '@/components/icons/DoorIcon';
import DashboardCircleIconSolid from '@/components/icons/DashboardCircleIconSolid';
import ApartmentsLookupV2 from '@/features/apartments/components/ApartmentsLookupV2';
import SettlementsTypePicker from './SettlementsTypePicker';
import { SettlementsType } from '../../types/finances-dto';
import SettlementsDetailsEmpty from './SettlementsDetailsEmpty';

type FormData = z.infer<typeof FinancesPageFilterFormSchema>;

type Props = {
    filterType?: SettlementsType;
    setFilterType?: (type?: SettlementsType) => void;
    onClose: (state: boolean) => void;
};

const SettlementsDetails: FC<Props> = ({ filterType, setFilterType, onClose }) => {
    const [isOpen, setIsOpen] = useState(true);

    const isMobile = useMediaQuery(AppConfig.mediaQueries.mobile);
    const { t } = useTranslation();

    const { balanceData, isLoadingBalanceData } = useFinancesBalanceApi();

    const { filter, setFilter } = useFinancesPageFilter();

    const { control, watch } = useForm<FormData>({
        resolver: zodResolver(FinancesPageFilterFormSchema),
        values: {
            startDate: filter.startDate,
            endDate: filter.endDate,
            apartmentId: filter.apartmentId,
        },
    });

    const selectedApartmentId = watch('apartmentId');

    useEffect(() => {
        if (selectedApartmentId !== undefined && selectedApartmentId !== filter.apartmentId) {
            setFilter({
                ...filter,
                apartmentId: selectedApartmentId,
            });
        }
    }, [filter, selectedApartmentId, setFilter]);

    const handleClose = () => {
        setIsOpen(false);
        setTimeout(onClose, 300);
    };

    return (
        <Sheet open={isOpen} onOpenChange={handleClose}>
            <SheetContent className={cn('flex flex-col', isMobile ? 'w-full' : 'w-160')}>
                <SheetHeader>
                    <SheetDescription />
                    <SheetTitle>
                        {!isMobile && (
                            <SheetClose
                                className={cn(
                                    'flex items-center justify-center cursor-pointer',
                                    isMobile && 'size-11 border border-popover-100 rounded-full',
                                )}
                            >
                                <MoveLeftIcon />
                            </SheetClose>
                        )}
                        {t('main.finances.components.settlements.components.details.title')}
                        {isMobile && (
                            <SheetClose className="size-11 ml-auto flex items-center justify-center cursor-pointer border border-popover-100 rounded-full">
                                <Cancel_01_Icon />
                            </SheetClose>
                        )}
                    </SheetTitle>
                </SheetHeader>
                <div className="flex-1 flex flex-col bg-popover-30 overflow-hidden select-none">
                    <div
                        className={cn(
                            'h-full p-6 flex flex-col gap-6 overflow-y-scroll no-scrollbar',
                            isMobile && 'p-4',
                        )}
                    >
                        <div className="flex gap-4">
                            <SettlementsTypePicker value={filterType} onSelect={setFilterType} />
                            <ApartmentsLookupV2
                                control={control}
                                fieldName={'apartmentId'}
                                selectVariant="multi"
                                label=""
                                placeholder={t('All apartments')}
                                containerClassName="min-w-36 max-w-60 w-fit"
                                buttonClassName="h-9 rounded-full!"
                                addResetButton={true}
                            />
                        </div>
                        {isLoadingBalanceData && (
                            <>
                                <Skeleton className="w-16 h-3 my-1 rounded-semi-xl" />
                                {Array.from({ length: 5 }).map((_, index) => (
                                    <div key={index} className="flex flex-col gap-4">
                                        <div className="flex flex-col gap-4">
                                            <SettlementsDetailsSkeleton isMobile={isMobile} />
                                        </div>
                                    </div>
                                ))}
                            </>
                        )}
                        {!isLoadingBalanceData &&
                            !!balanceData?.apartments?.length &&
                            balanceData?.apartments?.map((apartment) => (
                                <div key={apartment.apartmentId} className="flex flex-col gap-4">
                                    <h5 className="text-sm text-popover-800">
                                        {apartment.apartmentName}
                                    </h5>
                                    <div className={cn('flex flex-col gap-4', isMobile && 'gap-2')}>
                                        {(filterType === 'property' || !filterType) && (
                                            <SettlementsElement
                                                label="property"
                                                value={balanceData?.property.toFixed(2)}
                                                icon={DoorIcon}
                                                onClick={() => {}}
                                                isLoading={isLoadingBalanceData}
                                                isMobile={isMobile}
                                                type="pay"
                                            />
                                        )}
                                        {(filterType === 'otherServices' || !filterType) && (
                                            <SettlementsElement
                                                label="otherService"
                                                value={balanceData?.otherServices.toFixed(2)}
                                                icon={DashboardCircleIconSolid}
                                                onClick={() => {}}
                                                isLoading={isLoadingBalanceData}
                                                isMobile={isMobile}
                                                type="pay"
                                            />
                                        )}
                                    </div>
                                </div>
                            ))}
                        {!isLoadingBalanceData && !balanceData?.apartments?.length && (
                            <SettlementsDetailsEmpty />
                        )}
                    </div>
                </div>
                <Toaster />
            </SheetContent>
        </Sheet>
    );
};

export default SettlementsDetails;
